// Metas do Comercial — uma fonte por nível (decisão de 29/10/2026).
//
// - Meta do TIME: o kWh aprovado no mês, todos os canais juntos (Comercial, Parceiros,
//   Empresa Parceira e Licitação). Mora em `metas_mensais`, indicador `com_kwh` — a
//   mesma linha que a aba Metas do Painel Executivo edita e que a Visão Geral (Diretoria,
//   Gestão Comercial) e os KPIs da aba CEO leem. O Analytics Comercial lê e edita essa
//   mesma linha, então mudar num lugar muda nos outros. Regra do mês: `metaDoMes` (mês
//   sem linha repete a última definida); sem linha nenhuma, vale a meta do KPI validado
//   na aba CEO — a mesma ordem de `avaliarKpi` (src/lib/visaoGeral/diretoria.ts).
// - Meta por PESSOA: a da Folha de Pagamento (`comissao_vendedores.meta_kwh`, repetindo
//   a última definida — RPC `parametros_comissao_mes`). É a que paga comissão; só se
//   edita na Folha. Analytics (com filtro de canal ou pessoa), Performance Comercial,
//   Visão Geral e Meu Trabalho leem dela.
//
// Até esta data havia também 130.000 kWh fixos no código do Analytics (editáveis só em
// memória) e a aba Metas do Performance Comercial (`metas_comerciais`), parada desde
// jun/2026. As duas saíram.
//
// Funções puras: rodam sob `node --test` com type-stripping.

import { INDICADOR_ENERGIA_COMERCIAL, metaDoMes, rotuloDoMes, type MetaMensal } from './dashboard/metasMensais.ts';

export type OrigemMetaDoTime = 'mes' | 'herdada' | 'validada';

export interface MetaDoTime {
  valor: number;
  /** 'mes' = definida para o mês; 'herdada' = repete a de um mês anterior; 'validada' = KPI da aba CEO. */
  origem: OrigemMetaDoTime;
  /** 'AAAA-MM-01' do mês em que a linha foi definida (null quando vem do KPI validado). */
  desde: string | null;
}

/**
 * A meta do time no mês ('AAAA-MM'), ou null se ninguém definiu. `kpiValidado` é o
 * KPI `com_kwh` validado na aba CEO (kpis_departamento), usado só sem nenhuma linha.
 */
export function metaDoTimeNoMes(
  linhas: readonly MetaMensal[],
  kpiValidado: { meta: number | string | null } | null | undefined,
  mes: string,
): MetaDoTime | null {
  const doMes = metaDoMes(linhas, INDICADOR_ENERGIA_COMERCIAL, mes);
  if (doMes) return { valor: doMes.valor, origem: doMes.herdada ? 'herdada' : 'mes', desde: doMes.desde };
  const validada = Number(kpiValidado?.meta);
  if (kpiValidado && Number.isFinite(validada) && validada > 0) return { valor: validada, origem: 'validada', desde: null };
  return null;
}

/** Frase curta de onde veio a meta do time, para mostrar ao lado do número. */
export function origemDaMetaDoTime(m: MetaDoTime): string {
  if (m.origem === 'mes') return 'definida para o mês';
  if (m.origem === 'herdada' && m.desde) return `repete a de ${rotuloDoMes(m.desde)}`;
  return 'meta validada na aba CEO do Painel Executivo';
}

/** Soma das metas da Folha de quem está no recorte (quem não tem meta conta zero). */
export function somarMetasIndividuais(metas: ReadonlyMap<string, number> | null | undefined, ids: readonly string[]): number {
  if (!metas) return 0;
  return [...new Set(ids)].reduce((s, id) => s + (Number(metas.get(String(id))) || 0), 0);
}

/** 'AAAA-MM' de um mês/ano numérico (mes 1–12). */
export const mesIso = (mes: number, ano: number): string => `${ano}-${String(mes).padStart(2, '0')}`;
